/**
 * Ilustración de sparkles animada de la pantalla de generación (Figma node 71:4052).
 *
 * Vivía en `components/onboarding/`; se mudó acá al unificar la pantalla de carga de
 * la app con la del onboarding (ADR-2026-08-02-pantalla-carga-unificada) — ahora la
 * usan las dos, y el loader de la app no debería depender de la carpeta del onboarding.
 *
 * Réplica en CSS de la animación de Figma (motion), ciclo de 2.5s en loop (ADR-724):
 * - Estrella grande (centro): gira una vuelta completa (0→-360°) en el primer ~40%
 *   del ciclo y luego se mantiene.
 * - Estrella chica (abajo-derecha): escala 1→0→1 + fade (se "apaga" al medio).
 * - Estrella chica (arriba-izquierda): escala 0→1→0 + fade (se "enciende", fase opuesta).
 */

// Path de la estrella de 4 puntas (sparkle), viewBox 84×84 (exportado del DS).
const SPARKLE_PATH =
  'M41.6022 0.011748C44.4934 -0.153396 45.6442 1.43691 46.6685 3.87664C48.0787 7.23585 49.4452 10.7777 50.9159 14.097C51.9485 16.4338 53.2156 18.6595 54.6975 20.74C58.12 25.5778 62.6492 29.5271 67.9063 32.2585C70.2865 33.495 73.5034 34.8193 76.0156 35.9352L79.4723 37.4658C80.2407 37.8052 81.4835 38.2983 82.1228 38.7801C83.123 39.5309 83.7797 40.6525 83.9453 41.8928C84.3333 44.7926 82.6151 46.5034 80.14 47.441C78.9499 47.8919 77.5427 48.5755 76.3677 49.1095C71.9809 51.1024 67.5056 52.7538 63.5277 55.5219C58.3968 59.0809 54.2354 63.8649 51.4203 69.4409C50.3512 71.5443 49.3754 73.9804 48.4112 76.1632C47.9456 77.2166 47.4866 78.273 47.034 79.3316C46.0028 81.7219 45.3385 83.6294 42.4068 83.9864C40.6185 84.1254 38.7225 83.1925 37.9568 81.5532C36.2891 77.9821 34.8356 74.2689 33.182 70.692C32.0975 68.3713 30.7898 66.1618 29.2772 64.0949C25.8777 59.4587 21.448 55.6766 16.3376 53.0465C13.8982 51.7891 10.6512 50.4484 8.0954 49.3148L4.62835 47.7803C3.69807 47.3686 2.66842 47.0042 1.84782 46.394C0.834155 45.6403 0.219432 44.5392 0.0483643 43.2931C-0.163531 41.7497 0.323092 40.1978 1.46399 39.1193C2.48415 38.1549 5.97419 36.8284 7.42364 36.1856C11.9397 34.1829 16.6002 32.387 20.6264 29.4685C25.6301 25.8582 29.6724 21.075 32.3989 15.5379C33.6155 13.0675 34.5359 10.4631 35.6432 7.93426C36.4583 6.07288 37.3487 3.36672 38.4375 1.69187C39.0524 0.746147 40.4693 0.143284 41.6022 0.011748Z';

const CSS = `
@keyframes atlas-sparkle-spin {
  0% { transform: rotate(0deg); }
  39.88% { transform: rotate(-360deg); }
  100% { transform: rotate(-360deg); }
}
@keyframes atlas-sparkle-out {
  0% { transform: scale(1); opacity: 1; }
  49.9% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes atlas-sparkle-in {
  0% { transform: scale(0); opacity: 1; }
  49.9% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .atlas-sparkle { animation: none !important; }
}
`;

function Sparkle({ className, style }: { className?: string; style?: React.CSSProperties }) {
  // El tamaño/posición se define en un `<span>` de bloque vía `inset-*`. El SVG
  // lo rellena con `size-full`. Antes el `<svg>` (elemento reemplazado) se
  // dimensionaba sólo por los `inset-*`: Chromium resuelve la caja desde los
  // insets, pero WebKit/Safari (el webclip de la PWA) los ignora para el tamaño
  // y cae a la medida por defecto del SVG → estrellas gigantes y rotas.
  return (
    <span className={`atlas-sparkle absolute block ${className ?? ''}`} style={style}>
      {/* `overflow-visible`: el path excede levemente el viewBox (puntos en ~84.3 y ~-0.15),
          y el `<svg>` recorta por defecto → se cortaban las puntas superior/derecha. */}
      <svg viewBox="0 0 84 84" fill="currentColor" aria-hidden className="block size-full overflow-visible">
        <path d={SPARKLE_PATH} />
      </svg>
    </span>
  );
}

/**
 * Una sola estrella, estática, para usar como ícono suelto (ej. las tarjetas de
 * consejo del marquee). Hereda el color con `currentColor` y el tamaño de la clase.
 */
export function SparkleGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 84 84"
      fill="currentColor"
      aria-hidden
      className={`block shrink-0 overflow-visible ${className ?? ''}`}
    >
      <path d={SPARKLE_PATH} />
    </svg>
  );
}

/**
 * Variante compacta para usar como ícono animado inline (ej. el botón
 * "Agrupar con IA" del modal de carga masiva mientras el backend agrupa):
 * misma coreografía que la pantalla de carga, pero el tamaño y el color los
 * pone el caller (`size-* text-*`). Sin delay: en un botón el estado ya
 * arrancó cuando esto se monta.
 */
export function SparklesLoaderInline({ className }: { className?: string }) {
  return (
    <div className={`relative ${className ?? ''}`} aria-hidden>
      <style>{CSS}</style>
      <Sparkle
        className="inset-[23.75%]"
        style={{ animation: 'atlas-sparkle-spin 2.5s ease-in-out infinite backwards' }}
      />
      <Sparkle
        className="inset-[66.25%_11.25%_11.25%_66.25%]"
        style={{ animation: 'atlas-sparkle-out 2.5s cubic-bezier(0.5,0,0.5,1) infinite backwards' }}
      />
      <Sparkle
        className="inset-[15.63%_69.38%_67.5%_13.75%]"
        style={{ animation: 'atlas-sparkle-in 2.5s cubic-bezier(0.5,0,0.5,1) infinite backwards' }}
      />
    </div>
  );
}

export interface SparklesLoaderProps {
  className?: string;
  /**
   * Demora (ms) antes de que **arranque** la animación. Durante la espera la
   * ilustración se muestra **igual** (estática en su frame inicial, gracias a
   * `animation-fill-mode: backwards`), sin quedar en blanco. Default 0.
   * En la pantalla de generación se usa 2s para sincronizar con el "hold" de la
   * barra (ADR-744).
   */
  startDelayMs?: number;
}

export function SparklesLoader({ className, startDelayMs = 0 }: SparklesLoaderProps) {
  // `backwards` = durante el delay se pinta el keyframe 0% (frame de reposo), así la
  // imagen se ve desde el arranque y al cumplirse el delay la animación continúa sin salto.
  const delay = `${startDelayMs}ms`;
  return (
    <div className={`relative size-40 text-white ${className ?? ''}`} aria-hidden>
      <style>{CSS}</style>
      {/* Estrella grande (centro) — gira una vuelta y mantiene. */}
      <Sparkle
        className="inset-[23.75%]"
        style={{ animation: `atlas-sparkle-spin 2.5s ease-in-out ${delay} infinite backwards` }}
      />
      {/* Estrella chica (abajo-derecha) — se apaga al medio. */}
      <Sparkle
        className="inset-[66.25%_11.25%_11.25%_66.25%]"
        style={{ animation: `atlas-sparkle-out 2.5s cubic-bezier(0.5,0,0.5,1) ${delay} infinite backwards` }}
      />
      {/* Estrella chica (arriba-izquierda) — se enciende al medio (fase opuesta). */}
      <Sparkle
        className="inset-[15.63%_69.38%_67.5%_13.75%]"
        style={{ animation: `atlas-sparkle-in 2.5s cubic-bezier(0.5,0,0.5,1) ${delay} infinite backwards` }}
      />
    </div>
  );
}

export default SparklesLoader;
